/**
 * "Mais opções": tela cheia, janela flutuante e, nas salas por link, "Sair da sala". A janela
 * flutuante leva a sala inteira para fora da guia (Chrome e Edge no desktop); onde o navegador não
 * tem isso, cai no picture-in-picture só do vídeo do outro participante. Cada item só existe onde
 * faz sentido (o iPhone não tem tela cheia de elemento); sem nenhum item, o botão nem aparece. O
 * link é copiado pelo botão de compartilhar, que fica à vista na barra. Numa chamada com a Jev, aqui também ficam as
 * legendas (um recurso de uso eventual, que não merece botão na barra).
 */
import { Captions, EllipsisVertical, LogOut, Maximize2, PictureInPicture2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { captionsEnabled } from '../transcript/LiveCaptions';
import { useCallTranscript } from '../transcript/transcriptContext';
import { ROUND_BUTTON, SIZE } from './controlStyles';
import { RoomMenu, RoomMenuContent, RoomMenuItem, RoomMenuSeparator, RoomMenuTrigger } from './roomMenu';

// O documento é o da sala: com ela na janela flutuante, não é o da guia.
const docOf = (root) => root?.ownerDocument ?? (typeof document !== 'undefined' ? document : null);

/** Entra ou sai da tela cheia com o elemento da sala (o mesmo onde os menus abrem). */
export function toggleFullscreen(root) {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else root?.requestFullscreen?.();
}

/** Entra ou sai do picture-in-picture com o vídeo do outro participante (navegadores sem janela flutuante). */
export function togglePictureInPicture(root) {
    const video = root?.querySelector('video[data-remote-video]');
    if (!video) return;
    if (document.pictureInPictureElement) document.exitPictureInPicture?.();
    else video.requestPictureInPicture?.();
}

/**
 * @param {{
 *   variant: keyof typeof SIZE, root: HTMLElement | null, onLeaveRoom?: () => void,
 *   popout?: { supported: boolean, isOpen: boolean, toggle: () => void },
 * }} props: `popout` liga a janela flutuante (a sala fora da guia).
 */
export function MoreMenu({ variant, root, onLeaveRoom, popout }) {
    const doc = docOf(root);
    const popoutAvailable = Boolean(popout?.supported);
    // Na janela flutuante não há tela cheia (a janela já é pequena de propósito).
    const fullscreen = !popout?.isOpen && doc?.fullscreenEnabled === true;
    const pictureInPicture = !popoutAvailable && doc?.pictureInPictureEnabled === true;
    const transcript = useCallTranscript();
    const captions = Boolean(transcript);
    const captionsOn = captions && captionsEnabled(transcript.captionsPref);
    if (!captions && !fullscreen && !popoutAvailable && !pictureInPicture && !onLeaveRoom) return null;
    const hasViewItems = fullscreen || popoutAvailable || pictureInPicture;

    return (
        <RoomMenu>
            <RoomMenuTrigger asChild>
                <button type="button" aria-label="Mais opções" className={cn(ROUND_BUTTON, SIZE[variant].btn)}>
                    <EllipsisVertical className={SIZE[variant].icon} aria-hidden="true" />
                </button>
            </RoomMenuTrigger>
            <RoomMenuContent align="center" className="min-w-[280px]">
                {captions && (
                    <RoomMenuItem icon={Captions} onSelect={() => transcript.setCaptionsPref(!captionsOn)}>
                        {captionsOn ? 'Desligar legendas' : 'Ligar legendas'}
                    </RoomMenuItem>
                )}
                {captions && hasViewItems && <RoomMenuSeparator />}
                {fullscreen && <RoomMenuItem icon={Maximize2} onSelect={() => toggleFullscreen(root)}>Tela cheia</RoomMenuItem>}
                {popoutAvailable && (
                    <RoomMenuItem
                        icon={PictureInPicture2}
                        description={popout.isOpen ? undefined : 'Mantém a chamada à vista fora desta guia'}
                        onSelect={popout.toggle}
                    >
                        {popout.isOpen ? 'Voltar para a guia' : 'Abrir em janela flutuante'}
                    </RoomMenuItem>
                )}
                {pictureInPicture && (
                    <RoomMenuItem icon={PictureInPicture2} onSelect={() => togglePictureInPicture(root)}>
                        Janela flutuante do navegador
                    </RoomMenuItem>
                )}
                {onLeaveRoom && (captions || hasViewItems) && <RoomMenuSeparator />}
                {onLeaveRoom && (
                    <RoomMenuItem icon={LogOut} description="O link continua valendo até expirar" onSelect={onLeaveRoom}>
                        Sair da sala
                    </RoomMenuItem>
                )}
            </RoomMenuContent>
        </RoomMenu>
    );
}
